import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { supabase } from '../../config/supabase'
import { showAlert } from '../../services/dialogService'
import Loader from '../../components/Loader/Loader'
import './Auth.css'

function ResetPassword() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [verifying, setVerifying] = useState(true)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  useEffect(() => {
    // Verificar que el usuario tiene una sesión válida para reset de contraseña
    // Supabase maneja esto automáticamente cuando el usuario hace clic en el link del email
    const checkSession = async () => {
      try {
        // Si hay tokens en la URL, Supabase los procesará automáticamente
        const accessToken = searchParams.get('access_token')
        const type = searchParams.get('type')
        
        // Verificar que es un link de reset de contraseña
        if (type !== 'recovery') {
          setError('Invalid reset link. Please request a new password reset.')
          setVerifying(false)
          return
        }
        
        if (accessToken) {
          // Establecer la sesión con el token del email
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: searchParams.get('refresh_token') || ''
          })
          
          if (sessionError) {
            setError('Invalid or expired reset link. Please request a new password reset.')
            setVerifying(false)
            return
          }
        } else {
          // Verificar si ya hay una sesión activa
          const { data: { session } } = await supabase.auth.getSession()
          if (!session) {
            setError('Invalid or expired reset link. Please request a new password reset.')
            setVerifying(false)
            return
          }
        }
        
        setVerifying(false)
      } catch (err) {
        console.error('Error verifying reset link:', err)
        setError('Error verifying reset link. Please try again.')
        setVerifying(false)
      }
    }

    checkSession()
  }, [searchParams])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Validar que las contraseñas coincidan
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    // Validar longitud de contraseña
    if (password.length < 6) {
      setError('Password must be at least 6 characters long.')
      return
    }

    setLoading(true)

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      })

      if (updateError) throw updateError

      // Contraseña actualizada exitosamente
      await showAlert('Password updated successfully! You can now sign in with your new password.')
      navigate('/login')
    } catch (err) {
      setError(err.message || 'Error updating password. Please try again.')
      console.error('Reset password error:', err)
    } finally {
      setLoading(false)
    }
  }

  if (verifying) {
    return <Loader />
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <p className="auth-welcome-text">
          Create a new password for your account. Make sure it's something you'll remember!
        </p>
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter new password"
                minLength="6"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm new password"
                minLength="6"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Updating...' : 'Update Password'}
          </button>

          <p className="auth-footer-text">
            Remember your password? <Link to="/login" className="auth-link">Sign In</Link>
          </p>
        </form>
      </div>
    </div>
  )
}

export default ResetPassword
